import React, { useState } from 'react';
import PieChart from './PieChart';

const SkillDrilldownView = ({ people, capabilities, coverageCalculator }) => {
  const [selectedSkill, setSelectedSkill] = useState(null);
  
  const allSkills = coverageCalculator.getAllSkills(people, capabilities);

  const getSkillAnalysis = (skill) => {
    const peopleWithSkill = people.filter(person => 
      person.skills && person.skills.includes(skill)
    );
    
    const capabilitiesRequiringSkill = capabilities.filter(capability => 
      capability.required_skills && capability.required_skills.includes(skill)
    );

    const skillCoverage = capabilitiesRequiringSkill.length > 0 
      ? Math.round((peopleWithSkill.length / capabilitiesRequiringSkill.length) * 100)
      : 100;

    return {
      skill,
      peopleWithSkill,
      capabilitiesRequiringSkill,
      skillCoverage: Math.min(skillCoverage, 100),
      totalPeople: people.length,
      adoptionRate: Math.round((peopleWithSkill.length / people.length) * 100)
    };
  };

  const skillAnalytics = allSkills.map(getSkillAnalysis).sort((a, b) => b.adoptionRate - a.adoptionRate);

  const SkillDetailModal = ({ analysis, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-[90vw] max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">{analysis.skill}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl font-bold"
          >
            ×
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="text-center">
            <PieChart percentage={analysis.adoptionRate} size={80} />
            <p className="text-sm text-gray-600 mt-2">Team Adoption Rate</p>
          </div>
          <div className="text-center">
            <PieChart percentage={analysis.skillCoverage} size={80} />
            <p className="text-sm text-gray-600 mt-2">Capability Coverage</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6 w-full">
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">
              People with this skill ({analysis.peopleWithSkill.length})
            </h4>
            <div className="space-y-2">
              {analysis.peopleWithSkill.map((person, index) => (
                <div key={index} className="bg-blue-50 p-2 rounded border">
                  <div className="font-medium text-blue-900">{person.name}</div>
                  <div className="text-xs text-blue-600">
                    {person.skills?.length || 0} total skills
                  </div>
                </div>
              ))}
              {analysis.peopleWithSkill.length === 0 && (
                <p className="text-gray-500 italic">No one currently has this skill</p>
              )}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-3">
              Capabilities requiring this skill ({analysis.capabilitiesRequiringSkill.length})
            </h4>
            <div className="space-y-2">
              {analysis.capabilitiesRequiringSkill.map((capability, index) => (
                <div key={index} className="bg-green-50 p-2 rounded border">
                  <div className="font-medium text-green-900">{capability.name}</div>
                  <div className="text-xs text-green-600">
                    Requires {capability.required_skills?.length || 0} skills
                  </div>
                </div>
              ))}
              {analysis.capabilitiesRequiringSkill.length === 0 && (
                <p className="text-gray-500 italic">No capabilities require this skill</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">{analysis.peopleWithSkill.length}</div>
              <div className="text-sm text-gray-600">People</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{analysis.capabilitiesRequiringSkill.length}</div>
              <div className="text-sm text-gray-600">Capabilities</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{analysis.adoptionRate}%</div>
              <div className="text-sm text-gray-600">Adoption</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!allSkills.length) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Skill Analysis</h2>
        <p className="text-gray-500">No skills data available. Please add people and capabilities in the Editor.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Skill Analysis Dashboard</h2>
        <p className="text-sm text-gray-500 mt-1">
          Click on any skill to see detailed analysis and coverage information
        </p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 w-full">
          {skillAnalytics.map((analysis, index) => (
            <div
              key={index}
              onClick={() => setSelectedSkill(analysis)}
              className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-lg transition-shadow bg-white"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 text-sm">{analysis.skill}</h3>
                <PieChart percentage={analysis.adoptionRate} size={32} strokeWidth={3} />
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Team adoption:</span>
                  <span className="font-medium">{analysis.adoptionRate}%</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">People with skill:</span>
                  <span className="font-medium">{analysis.peopleWithSkill.length}/{analysis.totalPeople}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Used in capabilities:</span>
                  <span className="font-medium">{analysis.capabilitiesRequiringSkill.length}</span>
                </div>
              </div>
              
              <div className="mt-3 pt-2 border-t border-gray-100">
                <div className="text-xs text-blue-600 font-medium">Click for details →</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedSkill && (
        <SkillDetailModal
          analysis={selectedSkill}
          onClose={() => setSelectedSkill(null)}
        />
      )}
    </div>
  );
};

export default SkillDrilldownView;